import { useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { SortContext } from 'libs/control';
import { TableHeadCell, ResizeHandle } from './table-header-field.styled';

export const TableHeaderField = (props) => {
  const { sortField, name, title, numeric, align, orderBy, order, resizable, onResize, onResizeEnd, isHidden, ...rest } = props;
  // console.log("sortField", sortField);
  delete rest.render;
  delete rest.clip;
  delete rest.fullh;

  const thRef = useRef(null);
  const isResizingRef = useRef(false);
  const [enforcedMinWidth, setEnforcedMinWidth] = useState(null);

  /**
   * minimum width (px) for the header cell so its title (and sort arrow) render on a single
   * line, measured live from the DOM so it stays correct regardless of font/theme/padding.
   */
  const measureMinWidth = () => {
    const th = thRef.current;
    const contentDiv = th && th.firstElementChild;
    const pEl = contentDiv && contentDiv.querySelector('p');
    if (!contentDiv || !pEl) {
      return null;
    }

    const prevDivWidth = contentDiv.style.width;
    const prevWhiteSpace = pEl.style.whiteSpace;
    pEl.style.whiteSpace = 'nowrap';
    contentDiv.style.width = 'max-content';
    const contentWidth = contentDiv.getBoundingClientRect().width;
    contentDiv.style.width = prevDivWidth;
    pEl.style.whiteSpace = prevWhiteSpace;

    const thStyle = window.getComputedStyle(th);
    const horizontalChrome =
      (parseFloat(thStyle.paddingLeft) || 0) + (parseFloat(thStyle.paddingRight) || 0) + (parseFloat(thStyle.borderLeftWidth) || 0) + (parseFloat(thStyle.borderRightWidth) || 0);

    return Math.ceil(contentWidth + horizontalChrome);
  };

  // guards against a width persisted (e.g. in localStorage) before this minimum-width rule
  // existed, or one that's stale because the title text/font changed since it was saved
  useLayoutEffect(() => {
    if (!resizable) {
      return;
    }
    const measured = measureMinWidth();
    if (measured == null) {
      return;
    }
    setEnforcedMinWidth(measured);

    const currentWidth = typeof rest.width === 'number' ? rest.width : parseFloat(rest.width);
    if (onResizeEnd && Number.isFinite(currentWidth) && currentWidth < measured) {
      onResizeEnd(measured);
    }
  }, [title, resizable, numeric, align, rest.width]);

  /**
   * start column resize by dragging
   * @param {*} event
   */
  const handleResizeStart = (event) => {
    event.preventDefault();
    event.stopPropagation();

    isResizingRef.current = true;
    document.body.classList.add('col-resizing');

    const startX = event.clientX;
    const startWidth = thRef.current ? thRef.current.getBoundingClientRect().width : typeof rest.width === 'number' ? rest.width : 150;
    const minWidth = measureMinWidth() ?? 0;
    let finalWidth = startWidth;
    let rafId = null;

    const applyPreview = () => {
      rafId = null;
      if (onResize) {
        onResize(finalWidth);
      }
    };

    const handleMouseMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      finalWidth = Math.max(minWidth, startWidth + delta);
      // throttle to one update per animation frame - keeps the drag smooth on large grids
      if (rafId === null) {
        rafId = requestAnimationFrame(applyPreview);
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.classList.remove('col-resizing');
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      if (onResizeEnd) {
        onResizeEnd(finalWidth);
      }
      // defer clearing the flag until after the click event (fired right after mouseup) has been handled,
      // so the sort handler below can ignore the click that follows a resize drag
      setTimeout(() => {
        isResizingRef.current = false;
      }, 0);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <SortContext.Consumer>
      {({ setSort, getSortArrow }) => {
        const other = {};
        if (sortField) {
          other.onClick = () => {
            if (isResizingRef.current) {
              return;
            }
            setSort(props.sortField);
          };
        }

        return (
          <TableHeadCell
            ref={thRef}
            {...rest}
            withSort={!!sortField}
            className={props.className}
            $numeric={numeric}
            $resizable={resizable}
            align={align}
            style={enforcedMinWidth ? { minWidth: `${enforcedMinWidth}px` } : undefined}
            {...other}
          >
            <div>
              <p>{title}</p>

              {sortField && getSortArrow && getSortArrow(props.sortField)}
            </div>
            {resizable && <ResizeHandle data-resize-handle onMouseDown={handleResizeStart} onClick={(event) => event.stopPropagation()} />}
          </TableHeadCell>
        );
      }}
    </SortContext.Consumer>
  );
};

TableHeaderField.propTypes = {
  title: PropTypes.node.isRequired,
  name: PropTypes.string,
  sortField: PropTypes.string,
  numeric: PropTypes.bool,
  align: PropTypes.string,
  width: PropTypes.any,
  render: PropTypes.func,
  resizable: PropTypes.bool,
  onResize: PropTypes.func,
  onResizeEnd: PropTypes.func,
};

TableHeaderField.defaultProps = {
  numeric: false,
};

export default TableHeaderField;
